import { Combobox } from '@headlessui/react';
import { ClockIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { format, addMinutes, startOfDay, parse } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';

interface TimePickerProps {
  label?: string;
  error?: string;
  className?: string;
  id?: string;
  required?: boolean;
  disabled?: boolean;
  name: string;
  value?: string;
  onChange?: (value: string | null) => void;
  interval?: 15 | 30;
}

export function TimePicker({
  label,
  error: propError,
  className,
  id,
  required,
  disabled,
  name,
  value: propValue,
  onChange: propOnChange,
  interval = 30,
}: TimePickerProps): JSX.Element {
  const form = useFormContext();
  const error = propError || (form?.formState?.errors[name]?.message as string);
  const value = form ? (form.watch(name) as string) : propValue;

  // Move timeOptions outside component or memoize
  const timeOptions = React.useMemo(() => {
    const times = [];
    const day = startOfDay(new Date());
    const intervalsPerHour = 60 / interval;
    const totalIntervals = 24 * intervalsPerHour;

    for (let i = 0; i < totalIntervals; i += 1) {
      const time = addMinutes(day, i * interval);
      times.push(format(time, 'hh:mm a'));
    }
    return times;
  }, [interval]);

  const [query, setQuery] = useState('');

  const filteredTimes = React.useMemo(
    () =>
      query === ''
        ? timeOptions
        : timeOptions.filter((time) =>
            time.toLowerCase().includes(query.toLowerCase()),
          ),
    [query, timeOptions],
  );

  const handleChange = React.useCallback(
    (newValue: string | null) => {
      if (newValue) {
        // Parse the selected time (in 12-hour format) to a Date object
        const parsedTime = parse(newValue, 'hh:mm a', new Date());

        // Format with timezone offset
        const timeWithZone = formatInTimeZone(
          parsedTime,
          'Asia/Kolkata',
          'HH:mm:ssxxx',
        );

        if (form) {
          form.setValue(name, timeWithZone, { shouldValidate: true });
        }
        propOnChange?.(timeWithZone);
      } else {
        if (form) {
          form.setValue(name, null, { shouldValidate: true });
        }
        propOnChange?.(null);
      }
      setQuery('');
    },
    [form, name, propOnChange],
  );

  // Extract display value from timezone-aware string
  const displayValue = React.useMemo(() => {
    if (!value) return '';
    try {
      // If it's already in the display format, return as is
      if (value.includes('AM') || value.includes('PM')) return value;

      // Parse the timezone-aware time string
      const date = parse(value.split(/[+-]/)[0], 'HH:mm:ss', new Date());
      return format(date, 'hh:mm a');
    } catch (err) {
      return value;
    }
  }, [value]);

  return (
    <div className="w-full space-y-1">
      {label && (
        <label
          htmlFor={id || name}
          className="flex-1 font-nunito text-sm font-normal leading-6 text-[#434343]"
        >
          {label}
          {required && <span className="text-[#CE3F4F] ml-1">*</span>}
        </label>
      )}
      <Combobox
        disabled={disabled}
        value={displayValue}
        onChange={handleChange}
        nullable
      >
        <div className="relative w-full">
          <Combobox.Button as="div" className="w-full">
            <Combobox.Input
              className={clsx(
                'flex h-10 w-full items-center justify-center',
                'px-3 py-[10px]',
                'rounded-[6px] border border-[#D9D9D9]',
                'bg-[#FEFDFC]',
                'font-roboto text-base font-normal leading-6',
                'text-[#141414]',
                'placeholder:text-[#8C8C8C]',
                'outline-none',
                'cursor-pointer',
                error && 'border-red-300',
                disabled &&
                  'bg-[var(--Color-Neutral-Fill-colorFillTertiary,rgba(0,0,0,0.04))] text-[var(--Text-text-disabled,#8C8C8C)] cursor-not-allowed',
                className,
              )}
              onChange={(event) => setQuery(event.target.value)}
              displayValue={(time: string) => time}
              readOnly
            />
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
              <ClockIcon
                className={clsx(
                  'h-5 w-5 cursor-pointer',
                  disabled ? 'text-[#8C8C8C]' : 'text-[#141414]',
                )}
                aria-hidden="true"
              />
            </div>
          </Combobox.Button>

          <Combobox.Options
            className={clsx(
              'absolute z-10 mt-1 max-h-60 w-full overflow-auto',
              'rounded-md bg-white py-1 text-base shadow-lg',
              'ring-1 ring-black ring-opacity-5',
              'focus:outline-none sm:text-sm',
              'custom-scrollbar',
            )}
          >
            {filteredTimes.map((time) => (
              <Combobox.Option
                key={time}
                value={time}
                className={({ active }) =>
                  clsx(
                    'relative cursor-pointer select-none py-2 px-4',
                    active ? 'bg-blue-600 text-white' : 'text-gray-900',
                  )
                }
              >
                {time}
              </Combobox.Option>
            ))}
          </Combobox.Options>
        </div>
      </Combobox>
      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
    </div>
  );
}
